// #############################################################################
// #
// #  Japanese translations
// #
// #############################################################################
// This file contains the Japanese translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for japanese-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to japanese-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langJa = {
  home: {
    welcome: '{service}サービスへようこそ',
    enter: '入る'
  },
  account_type: {
    publisher: '発行者',
    consumer: 'アグリゲーター',
    author: '著者'
  },
  app: {
    accountNotSubscribedToCurrentService: 'あなたのアカウントは現在のサービスに登録されていません'
  },
  login: {
    continue: '続ける',
    login_tab: 'ログイン',
    logout: 'ログアウト',
    register_tab: '登録',
    loginTitle: 'アカウントにサインイン',
    chooseSignInMethod: 'サインイン方法を選択',
    noAccount: 'アカウントをお持ちでないですか？',
    registerButton: 'アカウントを作成',
    cancel: 'キャンセル',
    identifier: '識別子',
    identifierDescription: 'メールアドレスで接続できます',
    identifierPlaceholder: 'メールアドレスを入力',
    usernameOrEmail: 'ユーザー名またはメールアドレス',
    useAnotherAccount: '別のアカウントを使用',
    username: 'ユーザー名',
    password: 'パスワード',
    email: 'メール',
    loginButton: 'ログイン',
    signingIn: 'サインイン中...',
    signInWithEmail: 'ワンタイムリンクを送信',
    signInWithEmailAgain: '新しいワンタイムリンクをリクエスト',
    signInWithEmailDescription: 'アカウントのメールアドレスを使用',
    signingInWithMagicLink: 'マジックリンクでサインイン中...',
    signInWithEmailMessageSent: 'ワンタイムリンクがメールに送信されました。リンクをクリックしてサインインしてください。',
    signingInWithEmail: 'ワンタイムリンクを送信中...',
    subscribeButton: '{type}に登録',
    registerAsButton: '{type}として登録',
    showPassword: 'パスワードを表示',
    hidePassword: 'パスワードを非表示',
    or: 'または',
    subscriptionSuccess: '登録に成功しました',
    alternativeAccountDescription: 'あなたのアカウントは現在のサービスに登録されていません。以下のサービスに登録するか切り替えることができます。',
    alternativeAccountGoTo: '{type}に移動',
    sameAccount: 'すでに同じアカウントでサインインしています。',
    sessionConflict: {
      title: 'セッションの競合',
      message: '別のアカウントのセッションが既に開いています。',
      currentSession: '現在のセッション:',
      magicLinkSession: 'マジックリンクセッション:',
      question: '現在のセッションを維持しますか、それともマジックリンクセッションに再接続しますか？',
      stayWithCurrent: '現在のセッションを維持',
      reconnectWithMagicLink: 'マジックリンクで再接続'
    },
    error: {
      invalidCredentials: '無効な認証情報',
      loginFailed: 'ログインに失敗しました',
      apiConnection: 'API接続エラー',
      subscribeFailed: '登録に失敗しました: {error}',
      signInWithEmailFailed: 'メールでのサインインに失敗しました: {error}（メールアドレスまたはユーザー名を確認してください）。',
      invalidEmailFormat: '有効なメールアドレスを入力してください'
    },
    alternativeAccount: {
      contentCreators: 'コンテンツクリエーターですか？',
      contentCreatorsLink: 'ここからログイン',
      authors: '著者ですか？',
      authorsLink: 'ここからログイン',
      aggregators: 'アグリゲーターですか？',
      aggregatorsLink: 'ここからログイン',
      author: '著者',
      consumer: 'アグリゲーター',
      publisher: '発行者'
    }
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    }
  },
  registration: {
    title: 'アカウント登録',
    introduction: {
      title: '登録へようこそ',
      subtitle: '{service}サービスに登録',
      description: '{service}サービスに登録しようとしています。この登録により、{service}アカウントで利用可能なすべての機能と機能にアクセスできます。',
      startRegistration: '登録を開始',
      alreadyHaveAccount: 'すでにアカウントをお持ちですか？ログイン'
    },
    step_introduction: {
      title: '紹介'
    },
    step_account_setup: {
      title: 'アカウント設定',
      displayName: '表示名',
      displayNamePlaceholder: '表示名を入力',
      username: 'ユーザー名',
      usernamePlaceholder: 'ユーザー名を入力',
      confirmPassword: 'パスワードを確認',
      passwordsDoNotMatch: 'パスワードが一致しません',
      password: 'パスワード',
      email: 'メール',
      emailPlaceholder: 'メールアドレスを入力',
      affiliation: '所属',
      affiliationPlaceholder: '大学、会社、または機関',
      homePage: 'ホームページ / メインページ',
      homePagePlaceholder: 'https://your-website.com',
      submit: 'プロフィールを送信',
      submitting: '送信中...'
    },
    step_account_verification: {
      title: 'アカウント確認',
      tokenLabel: '確認トークン',
      tokenPlaceholder: 'トークンを入力',
      checkEmail: '確認メールを送信しました。受信トレイを確認し、指示に従ってアカウントを確認してください。',
      tokenSent: '確認トークンを送信しました: {email}',
      resendToken: 'トークンを再送信',
      verifyAccount: 'アカウントを確認',
      sendingEmail: 'メールを送信中...',
      resendEmail: 'メールを再送信',
      accountVerified: 'アカウントの確認に成功しました！',
      redirecting: '次のステップにリダイレクト中...',
      verifying: 'アクティベーションを確認',
      sendingToken: '確認トークンを送信中...',
      invalidToken: '無効な確認トークンです。もう一度お試しください。',
      tooManyAttempts: '失敗した試行が多すぎます。新しいトークンをリクエストしてください。',
      noToken: '確認トークンが見つかりません。新しいトークンをリクエストしてください。',
      verificationSuccess: 'メール確認に成功しました！',
      attemptsRemaining: '残り{count}回の試行',
      resendSuccess: '新しい確認トークンをメールに送信しました。',
      networkError: 'ネットワークエラー。接続を確認して再試行してください。',
      checkingActivation: 'アカウントのアクティベーションを確認中...',
      emailSentTo: 'メール送信先:',
      activationEmailResent: 'アクティベーションメールを正常に送信しました！受信トレイを確認してください。',
      resendFailed: 'アクティベーションメールの送信に失敗しました。再試行してください。',
      missingIdentifierOrRegistrationToken: '識別子または登録トークンが不足しています',
      accountDoesNotHaveAuthorPrivileges: 'アカウントに著者の権限がありません。サポートにお問い合わせください。',
      accountActivationCheckFailed: 'アカウントのアクティベーション確認に失敗しました。再試行してください。',
      accountNotYetActivated: 'アカウントはまだアクティベートされていません。メールを確認し、アクティベーションリンクをクリックしてください。',
      invalidCredentials: '無効な認証情報です。識別子とパスワードを確認してください。',
      failedToVerifyAccountActivation: 'アカウントのアクティベーション確認に失敗しました。再試行してください。',
      failedToResendActivationEmail: 'アクティベーションメールの再送信に失敗しました。再試行してください。',
      accountRegistrationRejected: 'アカウント登録が拒否されました。サポートにお問い合わせください。'
    },
    step_registration_complete: {
      title: '登録を完了',
      registrationSummary: '登録の概要',
      profileCreated: 'プロフィールを作成しました',
      emailsSent: '発行者にメールを送信しました',
      continueToDashboard: 'ダッシュボードに続く',
      continueToLogin: 'ログインに続く'
    }
  },
  common: {
    cancel: 'キャンセル',
    save: '保存'
  },
  navbar: {
    docs: 'ドキュメント',
    account: 'アカウント',
    settings: '設定',
    logout: 'ログアウト',
    login: 'ログイン',
    pendingRegistrationDesc: 'このサービスにアクセスするには、登録を完了してください。',
    apps: {
      title: 'アプリ',
      publisher: '発行者',
      publisherDesc: '発行者サービスに切り替え',
      author: '著者',
      authorDesc: '著者サービスに切り替え',
      consumer: 'アグリゲーター',
      consumerDesc: 'アグリゲーターサービスに切り替え',
      website: 'ウェブサイト',
      websiteDesc: 'ウェブサイトに切り替え',
      portal: 'ポータル',
      portalDesc: 'ポータルに切り替え',
      currentApplicationDesc: '現在のサービス',
      unavailableServiceDesc: '利用できないサービス'
    }
  },
  sidebar: {
    expand: '展開',
    collapse: '折りたたむ',
    info: '情報',
    infoDesc: 'サービスについての情報',
    showDescriptions: '説明を表示',
    hideDescriptions: '説明を非表示'
  },
  pagination: {
    previous: '前へ',
    next: '次へ',
    first: '最初',
    last: '最後',
    page: 'ページ {current} から {total}',
    itemsPerPage: 'ページあたりの項目数'
  },
  table: {
    loading: '読み込み中...',
    noData: 'データなし',
    noResults: '結果なし',
    search: '検索'
  },
  form: {
    submit: '送信',
    cancel: 'キャンセル',
    save: '保存',
    loading: '読み込み中...'
  },
  modal: {
    close: '閉じる',
    confirm: '確認',
    cancel: 'キャンセル',
    save: '保存'
  },
  alert: {
    success: '成功',
    error: 'エラー',
    warning: '警告',
    info: '情報'
  },
  toast: {
    success: '成功',
    error: 'エラー',
    warning: '警告',
    info: '情報',
    close: '閉じる'
  },
  tooltip: {
    success: '成功',
    error: 'エラー',
    warning: '警告',
    info: '情報'
  },
  notification: {
    success: '成功',
    error: 'エラー',
    warning: '警告',
    info: '情報'
  },
  notifications: {
    title: '通知',
    empty: '通知なし',
    loading: '通知を読み込み中...',
    pageInfo: '{total}ページ中{current}ページ',
    error: '通知の読み込みエラー',
    markAsRead: '既読にする',
    markAsUnread: '未読にする',
    open: '開く',
    markAllAsRead: 'すべて既読にする',
    unread: '未読',
    all: 'すべて',
    noContent: 'コンテンツがありません',
    close: '閉じる',
    reload: '通知を再読み込み',
    showMore: 'もっと見る',
    loadingMore: 'さらに通知を読み込み中...',
    errorLoadingMore: '追加の通知の読み込みエラー',
    noMoreNotifications: 'これ以上の通知はありません',
    noNotifications: '通知なし',
    noNotificationsFound: '通知が見つかりません'
  },
  account: {
    title: 'アカウント情報',
    profile_section: 'プロフィール',
    api_keys_section: 'APIキー',
    security_section: 'セキュリティ',
    loading: 'アカウント情報を読み込み中...',
    profile: 'プロフィール',
    authorProfile: '著者プロフィール',
    publisherProfile: '発行者プロフィール',
    aggregatorProfile: 'アグリゲータープロフィール',
    id: 'ID',
    name: '名前',
    email: 'メール',
    services: 'サービス',
    memberSince: 'メンバー登録日',
    lastLogin: '最終ログイン',
    createNewApiKey: '新しいAPIキーを作成',
    apiKeysTitle: 'APIキー',
    apiKeys: {
      name: '名前',
      keyId: 'キーID',
      status: 'ステータス',
      lastUsed: '最終使用',
      created: '作成日',
      expires: '有効期限',
      neverUsed: '未使用',
      noKeys: 'APIキーが見つかりません。',
      advancedControls: '高度なコントロールについては、',
      advancedControlsLink: 'アカウント管理ページ'
    },
    security: {
      changePassword: 'パスワードを変更',
      changePasswordDescription: 'アカウントのパスワードを更新します。変更を確認するには現在のパスワードを入力してください。',
      currentPassword: '現在のパスワード',
      currentPasswordPlaceholder: '現在のパスワードを入力',
      newPassword: '新しいパスワード',
      newPasswordPlaceholder: '新しいパスワードを入力',
      confirmPassword: '新しいパスワードを確認',
      confirmPasswordPlaceholder: '新しいパスワードを再入力',
      changePasswordButton: 'パスワードを変更',
      changingPassword: '変更中...',
      passwordChangedSuccess: 'パスワードが正常に変更されました。',
      passwordChangeFailed: 'パスワードの変更に失敗しました。現在のパスワードを確認して再試行してください。',
      resetPassword: 'パスワードをリセット',
      resetPasswordDescription: 'パスワードをお忘れですか？登録済みのメールアドレスにリセットリンクを送信できます。メールのリンクをクリックして新しいパスワードを設定してください。',
      sendResetEmailButton: 'パスワードリセットメールを送信',
      sendingResetEmail: '送信中...',
      resetEmailSent: '登録済みのアドレスにパスワードリセットメールを送信しました。受信トレイを確認し、リンクに従って新しいパスワードを設定してください。',
      resetPasswordFailed: 'パスワードリセットメールの送信に失敗しました。再試行してください。',
      changePrimaryEmail: 'メインのメールアドレスを変更',
      changePrimaryEmailDescription: 'メインのメールアドレスを更新します。新しいアドレスに確認メールが送信されます。新しいメールは確認後にのみ有効になります。',
      newEmail: '新しいメール',
      newEmailPlaceholder: '新しいメールアドレスを入力',
      confirmEmail: '新しいメールを確認',
      confirmEmailPlaceholder: '新しいメールアドレスを再入力',
      emailsDoNotMatch: 'メールアドレスが一致しません',
      changeEmailButton: '確認メールを送信',
      sendingVerification: '送信中...',
      emailVerificationSent: '新しいアドレスに確認メールが送信されました。受信トレイを確認し、新しいメールを有効にする手順に従ってください。',
      emailChangeFailed: 'メール変更のリクエストに失敗しました。再試行してください。'
    }
  },
  profileSetup: {
    displayName: '表示名',
    displayNamePlaceholder: '表示名を入力',
    displayNameHelp: '表示名はシステム内であなたを識別するために使用されます（これはユーザー名ではありません）。',
    title: 'プロフィール設定',
    description: 'プロフィールを完了するために、あなたの専門情報を提供してください。',
    affiliation: '所属',
    affiliationPlaceholder: '例：ケンブリッジ大学、MIT、Google Research',
    areaOfInterest: '関心領域',
    areaOfInterestPlaceholder: '例：機械学習、コンピュータビジョン、NLP',
    homePage: 'ホームページ',
    homePagePlaceholder: 'https://your-website.com',
    secondaryEmails: 'セカンダリメール',
    addSecondaryEmail: 'セカンダリメールを追加',
    secondaryEmailPlaceholder: 'メールアドレス',
    secondaryEmailsHelp: 'あなたのセカンダリメール',
    save: 'プロフィールを保存',
    saving: '保存中...',
    nextPage: '次のページ',
    update: 'プロフィールを更新',
    updating: '更新中...',
    avatarUrl: 'アバターURL',
    avatarUrlPlaceholder: 'https://example.com/avatar.jpg',
    changeAvatar: 'アバターを変更',
    noAvatar: 'アバターなし',
    success: {
      saved: 'プロフィールを正常に保存しました！'
    },
    errors: {
      affiliationRequired: '所属は必須です。',
      areaOfInterestRequired: '関心領域は必須です。',
      invalidHomePage: 'ホームページの有効なURLを入力してください。',
      tooManySecondaryEmails: 'セカンダリメールは最大5つまで追加できます。',
      invalidSecondaryEmail: '有効なメールアドレスを入力してください。',
      saveFailed: 'プロフィールの保存に失敗しました。再試行してください。',
      invalidAvatarUrl: '有効なアバターURLを入力してください。'
    }
  },
  profile: {
    key_DISPLAY_NAME: '表示名',
    key_NAME: '名前',
    key_EMAIL: 'メール',
    key_AFFILIATION: '所属',
    key_AREA_OF_INTEREST: '関心領域',
    key_HOME_PAGE: 'ホームページ',
    key_EMAILS: 'メール',
    key_AVATAR_URL: 'アバターURL',
    key_ORCID: 'ORCID',
    key_LINKEDIN: 'LinkedIn',
    key_GOOGLE_SCHOLAR: 'Google Scholar'
  },
  profileSetupView: {
    title: 'プロフィール設定',
    description: 'このサービスにアクセスするには、プロフィールを設定してください。',
    update: 'プロフィールを更新',
    editProfile: 'プロフィールを編集'
  },
  bankInfoForm: {
    title: '銀行情報を追加/更新',
    accountHolder: '口座名義人:',
    iban: 'IBAN:',
    bic: 'BIC/SWIFTコード:',
    save: '銀行情報を保存',
    saved: '銀行情報をローカルに保存しました（このデモ用）。'
  },
  settings: {
    title: '設定',
    theme: 'テーマ',
    light: 'ライト',
    dark: 'ダーク',
    layout: 'レイアウト',
    helpEnabled: 'ヘルプメッセージを表示',
    sidebar: 'サイドバー',
    navbar: 'ナビゲーションバー',
    save: '保存',
    saved: '設定を保存しました！',
    language: '言語',
    experimentations: '実験',
    enabled: '有効',
    disabled: '無効'
  },
  apiKeyModal: {
    title: '新しいAPIキーを作成',
    namePlaceholder: 'APIキーの名前',
    nameHelp: 'APIキーの名前を入力してください。後でキーを識別するのに役立ちます。',
    createButton: 'APIキーを作成',
    yourKeyIs: '新しいAPIキーは:',
    copyWarning: 'このキーをコピーして保存してください。再度表示することはできません！',
    copy: 'コピー',
    copied: 'コピーしました！',
    close: '閉じる',
    error: 'APIキーの作成に失敗しました。'
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES'
  },
  theme: {
    switchToLight: 'ライトモードに切り替え',
    switchToDark: 'ダークモードに切り替え'
  }
};
